import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { array, number, object, string } from 'yup'
import { Col, Form as FormAntd, Modal, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import { SelectValue } from 'antd/lib/select'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import produce from 'immer'

import { ApplicationState } from 'src/store'
import { Button, Card as CardComponent } from 'src/components/Ant'
import { DatePicker, Radio, Select } from 'src/components/Formik'
import { getAir, getAirports, resetAir } from 'src/core/store/ducks/air/actions'
import {
  Id,
  Request,
  RequestQuoteAirRouteItinerary,
  Timestamp
} from 'src/types'
import { Option } from 'src/components/Formik/Select'
import { useStepsContext } from 'src/views/Requests/views/Form'
import { useT } from 'src/core/hooks/use-translation'
import Api from 'src/services/api/airports'

import { SecondaryText, StrongText } from '../../styles'

type SuggestionDate = {
  start_date?: number
  end_date?: number
}

type SearchAir = {
  destination: string
  end_date: Timestamp
  origin: string
  start_date: Timestamp
  type: RequestQuoteAirRouteItinerary | 'MULTI_STRETCH'
}

type Props = {
  request: Request
}

const initialValues: SearchAir = {
  destination: '',
  end_date: moment()
    .add(20, 'd')
    .hours(0)
    .minutes(0)
    .seconds(0)
    .millisecond(0)
    .unix(),
  origin: '',
  start_date: moment()
    .add(10, 'd')
    .hours(0)
    .minutes(0)
    .seconds(0)
    .millisecond(0)
    .unix(),
  type: 'ROUND_TRIP'
}

function Form({ request }: Props) {
  const dispatch = useDispatch()
  const stepsContext = useStepsContext()

  const [destinationCitiesList, setDestinationCitiesList] = useState<Option[]>(
    []
  )
  const [originCitiesList, setOriginCitiesList] = useState<Option[]>([])
  const [targetAirports, setTargetAirports] = useState<
    'destination' | 'origin'
  >()
  const refFormik = useRef(null)
  const refDestinationTimeout = useRef(null)
  const refOriginTimeout = useRef(null)

  const { air } = useSelector((state: ApplicationState) => ({ air: state.air }))

  const loading = useMemo(() => {
    return Object.values(air.searches).findIndex(item => item.loading) > -1
  }, [air.searches])

  useEffect(() => {
    if (originCitiesList.length || destinationCitiesList.length) {
      return
    }
    if (!request.request_id) {
      Object.keys(air.searches).some(key =>
        air.searches[key].list.some(resumeAirItem =>
          resumeAirItem.request_quote_airs.some(quoteAirItem =>
            quoteAirItem.request_quote_air_fares.some(async fareItem => {
              const hasAdded = !!request.full_request_air.filter(
                fullRequestAirItem =>
                  fullRequestAirItem.request_quote_air_fare_hash.includes(
                    fareItem.request_quote_fare_hash
                  )
              ).length
              if (hasAdded) {
                let response = await Api.getAirport({
                  name: quoteAirItem.air_origin,
                  organization_id: request.organization_id,
                  size: 1
                })

                if (response.status === 200) {
                  const originCity = response.data?.[0]
                  setOriginCitiesList([
                    {
                      label: `${originCity.name} (${originCity.iata_code})`,
                      value: originCity.iata_code
                    }
                  ])
                  if (refFormik.current) {
                    refFormik.current.setFieldValue(
                      'origin',
                      originCity.iata_code
                    )
                  }
                }

                response = await Api.getAirport({
                  name: quoteAirItem.air_destiny,
                  organization_id: request.organization_id,
                  size: 1
                })

                if (response.status === 200) {
                  const destinyCity = response.data?.[0]
                  setDestinationCitiesList([
                    {
                      label: `${destinyCity.name} (${destinyCity.iata_code})`,
                      value: destinyCity.iata_code
                    }
                  ])
                  if (refFormik.current) {
                    refFormik.current.setFieldValue(
                      'destination',
                      destinyCity.iata_code
                    )
                  }
                }
                return true
              }
            })
          )
        )
      )
      return
    }
    //if the request already exists, do nothing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const newList: Option[] = air.airportsList.map(airportItem => ({
      label: `${airportItem.name} (${airportItem.iata_code})`,
      value: airportItem.iata_code
    }))

    if (targetAirports === 'destination') {
      setDestinationCitiesList(newList)
    }

    if (targetAirports === 'origin') {
      setOriginCitiesList(newList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [air.airportsList])

  function renderDestinationField(
    formikProps: FormikProps<typeof initialValues>
  ) {
    return (
      <Select
        dataTestId="destination"
        filterOption={false}
        formikProps={formikProps}
        label="web.services.airs.form.destination.title"
        loading={targetAirports === 'destination' && air.airportsLoading}
        name="destination"
        onChange={(value: SelectValue) =>
          formikProps.setFieldValue('destination', value)
        }
        onSearch={value => {
          clearTimeout(refDestinationTimeout.current)
          refDestinationTimeout.current = setTimeout(() => {
            if (value) {
              setTargetAirports('destination')
              dispatch(getAirports(value))
            }
          }, 500)
        }}
        options={destinationCitiesList}
        placeholder="web.services.airs.form.destination.placeholder"
        showSearch
      />
    )
  }

  const getCustomerSupplierId = useCallback((): Id => {
    if (request.full_request_hotel && request.full_request_hotel.length) {
      return request.full_request_hotel[0].customer_supplier_id
    }
    if (request.full_request_vehicle && request.full_request_vehicle.length) {
      return request.full_request_vehicle[0].customer_supplier_id
    }
    const serviceTicket = request.request_tickets.find(ticketItem =>
      ['AIR', 'HOTEL', 'VEHICLE'].includes(ticketItem.request_ticket_type)
    )

    if (serviceTicket) {
      if (serviceTicket.request_ticket_hotel_selected) {
        return serviceTicket.request_ticket_hotel_selected
          .request_quote_hotel_room.request_quote_hotel.request_quote
          .customer_supplier_id
      }
      if (serviceTicket.request_ticket_vehicle_selected) {
        return serviceTicket.request_ticket_vehicle_selected
          .request_quote_vehicle.request_quote.customer_supplier_id
      }
    }

    return null
  }, [
    request.full_request_hotel,
    request.full_request_vehicle,
    request.request_tickets
  ])

  const suggestionDate = useMemo<SuggestionDate>(() => {
    const nextMonday = moment()
      .add(moment().isoWeekday() > 4 ? 2 : 1, 'weeks')
      .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
      .isoWeekday(1)
    const momentRequestOn = moment.unix(request.requested_on)
    const start_date =
      nextMonday > momentRequestOn ? nextMonday : momentRequestOn
    const end_date = start_date.clone().add(1, 'd')
    return { start_date: start_date.unix(), end_date: end_date.unix() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.requested_on])

  const [stretch, setStretch] = useState<SearchAir[]>([])
  const { t } = useT()

  return (
    <Formik
      initialValues={{
        ...initialValues,
        end_date: suggestionDate?.end_date,
        start_date: suggestionDate?.start_date
      }}
      onSubmit={(values, formikActions) => {
        const callSubmit = () => {
          dispatch(resetAir())

          stepsContext.setRequest(
            produce(request, draft => {
              draft.full_request_air = []
            })
          )

          if (values.type === 'MULTI_STRETCH') {
            setStretch([...stretch, { ...values }])
            formikActions.resetForm({
              ...initialValues,
              end_date: suggestionDate?.end_date,
              start_date: suggestionDate?.start_date,
              type: 'MULTI_STRETCH'
            })
            return
          }

          const customer_supplier_id = getCustomerSupplierId()
          stepsContext.setBudgetAllocations({
            standard_type: 'AIR',
            tickets: []
          })

          dispatch(
            getAir({
              reason_id:
                request.tmp_reason_id || request.request_user_data[0].reason_id,
              customer_supplier_id,
              searchKey: `${values.origin}-${values.destination}`,
              searchType:
                values.type === 'ROUND_TRIP' ? 'ROUND_TRIP' : 'ONE_WAY',
              date_end: values.type === 'ROUND_TRIP' ? values.end_date : null,
              date_ini: values.start_date,
              from_airport: values.origin,
              num_adults: request.request_user_data.length,
              to_airport: values.destination
            })
          )
        }

        if (request.full_request_air && request.full_request_air.length) {
          Modal.confirm({
            cancelText: t('common.actions.cancel'),
            content: t('web.services.airs.alert-clear-search'),
            onOk: () => callSubmit()
          })
          return
        }

        callSubmit()
      }}
      ref={refFormik}
      render={formikProps => {
        function handleChange(value: any) {
          let volta = moment.unix(formikProps.values.end_date)
          if (volta <= value) {
            volta = value.clone()
            formikProps.setFieldValue('end_date', volta.add(1, 'day').unix())
          }
        }
        return (
          <>
            <FormAntd onSubmit={formikProps.handleSubmit}>
              <Radio
                dataTestId="air-itinerary"
                formikProps={formikProps}
                name="type"
                onChange={e => {
                  const nextType = e.target.value
                  formikProps.setFieldValue('type', nextType)
                }}
                options={[
                  {
                    // disabled: disableOptionTip,
                    label: 'web.services.airs.form.types.one-way',
                    value: 'ONE_WAY'
                  },
                  {
                    // disabled: disableOptionTip,
                    label: 'web.services.airs.form.types.round-trip',
                    value: 'ROUND_TRIP'
                  },
                  {
                    // disabled: disableOptionTip,
                    label: 'web.services.airs.form.types.multi-segment',
                    value: 'MULTI_STRETCH'
                  }
                ]}
                required={false}
              />
              <Row gutter={24}>
                <Col
                  span={
                    ['ONE_WAY', 'MULTI_STRETCH'].includes(
                      formikProps.values.type
                    )
                      ? 9
                      : 18
                  }
                >
                  <Select
                    dataTestId="origin"
                    filterOption={false}
                    formikProps={formikProps}
                    label="web.services.airs.form.origin.title"
                    loading={targetAirports === 'origin' && air.airportsLoading}
                    name="origin"
                    onChange={(value: SelectValue) =>
                      formikProps.setFieldValue('origin', value)
                    }
                    onSearch={value => {
                      clearTimeout(refOriginTimeout.current)
                      refOriginTimeout.current = setTimeout(() => {
                        if (value) {
                          setTargetAirports('origin')
                          dispatch(getAirports(value))
                        }
                      }, 500)
                    }}
                    options={originCitiesList}
                    placeholder="web.services.airs.form.origin.placeholder"
                    showSearch
                  />
                </Col>
                {['ONE_WAY', 'MULTI_STRETCH'].includes(
                  formikProps.values.type
                ) && <Col span={9}>{renderDestinationField(formikProps)}</Col>}
                <Col span={6}>
                  <DatePicker
                    disabledDate={current =>
                      current && current < moment.unix(request.requested_on)
                    }
                    formikProps={formikProps}
                    label="common.startDate"
                    name="start_date"
                    onChange={handleChange}
                    placeholder="common.select-start-date"
                  />
                </Col>
              </Row>
              {formikProps.values.type === 'ROUND_TRIP' && (
                <Row gutter={24}>
                  <Col span={18}>{renderDestinationField(formikProps)}</Col>
                  <Col span={6}>
                    <DatePicker
                      disabledDate={current =>
                        current &&
                        current <
                          moment().hours(0).minutes(0).seconds(0).millisecond(0)
                      }
                      formikProps={formikProps}
                      label="common.endDate"
                      name="end_date"
                      placeholder="common.select-end-date"
                    />
                  </Col>
                </Row>
              )}
              <FormAntd.Item>
                <Button
                  block
                  data-test-id="air-action"
                  loading={
                    formikProps.values.type !== 'MULTI_STRETCH' && loading
                  }
                  htmlType="submit"
                  type={
                    formikProps.values.type === 'MULTI_STRETCH'
                      ? 'ghost'
                      : 'primary'
                  }
                  icon={
                    formikProps.values.type === 'MULTI_STRETCH'
                      ? 'plus'
                      : 'search'
                  }
                  title={`common.actions.${
                    formikProps.values.type === 'MULTI_STRETCH'
                      ? 'add'
                      : 'search'
                  }`}
                />
              </FormAntd.Item>
            </FormAntd>
            {formikProps.values.type === 'MULTI_STRETCH' && (
              <>
                <Row gutter={24} style={{ marginBottom: 16 }}>
                  {stretch.map((stretchItem, stretchItemIndex) => (
                    <Col span={8} key={stretchItemIndex}>
                      <CardComponent>
                        <div
                          style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'row',
                            justifyContent: 'space-evenly'
                          }}
                        >
                          <div>
                            <StrongText>{`${stretchItem.origin}-${stretchItem.destination}`}</StrongText>
                            <br />
                            <SecondaryText>
                              {moment
                                .unix(stretchItem.start_date)
                                .format('DD/MM/YYYY')}
                            </SecondaryText>
                          </div>
                          <Button
                            data-test-id="air-segment-remove"
                            icon="delete"
                            onClick={() =>
                              setStretch(
                                stretch.filter(
                                  stretchFilterItem =>
                                    `${stretchFilterItem.origin}-${stretchFilterItem.destination}` !==
                                    `${stretchItem.origin}-${stretchItem.destination}`
                                )
                              )
                            }
                            type="danger"
                          />
                        </div>
                      </CardComponent>
                    </Col>
                  ))}
                </Row>
                {!!stretch.length && (
                  <Button
                    data-test-id="air-multi-action"
                    icon="search"
                    loading={loading}
                    onClick={() => {
                      const customer_supplier_id = getCustomerSupplierId()
                      stepsContext.setBudgetAllocations({
                        standard_type: 'AIR',
                        tickets: []
                      })
                      stretch.forEach((stretchItem, stretchIndex) => {
                        dispatch(
                          getAir({
                            customer_supplier_id,
                            date_end: null,
                            date_ini: stretchItem.start_date,
                            day_end: stretch[stretch.length - 1].start_date,
                            day_ini: stretch[0].start_date,
                            from_airport: stretchItem.origin,
                            num_adults: request.request_user_data.length,
                            reason_id:
                              request.tmp_reason_id ||
                              request.request_user_data[0].reason_id,
                            searchKey: `${stretchIndex}-${stretchItem.origin}-${stretchItem.destination}`,
                            searchType: 'MULTI-STRETCH',
                            to_airport: stretchItem.destination
                          })
                        )
                      })
                    }}
                    title="common.actions.search"
                    type="primary"
                  />
                )}
              </>
            )}
          </>
        )
      }}
      validationSchema={object().shape({
        destination: string().trim().required('common.required'),
        end_date: number().required('common.required'),
        origin: string().trim().required('common.required'),
        segments: array().of(
          object().shape({
            date: number().required('common.required'),
            destination: string().trim().required('common.required'),
            origin: string().trim().required('common.required')
          })
        ),
        start_date: number().required('common.required')
      })}
    />
  )
}

export default Form
